// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import Axios from 'axios';

import JobRequest, { JobRequestDTO } from '@shared/models/job-request';
import BaseService from '@core/services/base';
import Collection, { CollectionDTO } from '@shared/models/collection';
import { OrderDTO } from '@shared/models/order';
import { AssetDTO } from '@shared/models/asset';
import { DeliverableDTO, DeliverableDueDate } from '@shared/models/deliverable';
import { DeliverableStatus } from '@shared/constants/deliverable';
import { ListRequestParams, ListRequestResponse } from '@shared/types/services';
import { REQUEST_PARAMS } from '@shared/constants/services';
import { SDeliverableInfoDTO, SDeliverableStatusChangeDTO } from '@shared/models/sp-sheets';
import { NotificationType, showNotification } from '@shared/components/Notification';
import { errObj2Str } from '@shared/utils/common';

@injectable()
export default class JobRequestsService extends BaseService<JobRequestDTO, JobRequest> {
  static diToken = Symbol('job-requests-service');

  constructor() {
    super({ domainArea: 'job-requests', Model: JobRequest });
  }

  getListQuery({ pagination, sorting, filtering }: ListRequestParams) {
    return `
      {
        jobRequests(
          ${REQUEST_PARAMS.pagination.fields.page}: ${pagination.page}, 
          ${REQUEST_PARAMS.pagination.fields.pageSize}: ${pagination.pageSize}, 
          sort: { field: ${sorting?.field}, type: ${sorting?.type}},
          ${REQUEST_PARAMS.filtering.fields.searchKeyword}: "${filtering?.searchKeyword || ''}",
        ) {
          items {
            id,
            name,
            dueDate,
            status,
            assetType
          }
          pageInfo {
            totalCount,
            hasPreviousPage,
            hasNextPage
          }
        }
      }
    `;
  }

  // Get job request info for a given id.  If there are extra args, the first one should be the block id.
  async get(id: JobRequestDTO['id'], ...args: Array<any>): Promise<JobRequest> {
    try {
      
      const url = args && args.length > 0 ? `/orders/assets/job-requests/${id}?${args[0]}` : `/orders/assets/job-requests/${id}`;
      const { data } = await Axios.get(url);

      return new JobRequest(data);
    } catch (err) { // Note: catch and throw the same exception is the same as not using try-catch.
      console.log("**** Exception in JobRequestsService::get:", err);
      throw err; 
    }
  }

  // Call service method to check if order needs to be synced.
  async syncOrderNeeded(orderId: number, lastRenderTime: Date): Promise<boolean> 
  {
    try {
      const url = 'orders/sync';

      console.log("*** send syncOrd request to:", url);
      const { data } = await Axios.post(url, JSON.stringify({ OrderId: orderId, LastRenderTime: lastRenderTime}));

      return data;
    } catch (err) {
        showNotification(errObj2Str(err), NotificationType.error);
      return false;
    }
  }

  async updateDeliverableInfo(
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequestId: JobRequestDTO['id'],
    deliverable: Record<string, unknown>,
    blockId: string = ''
  ) {

    const url = blockId ? 
                `orders/${orderId}/assets/${assetId}/job-requests/${jobRequestId}/deliverables/${deliverable.id}/status?${blockId}` :
                `orders/${orderId}/assets/${assetId}/job-requests/${jobRequestId}/deliverables/${deliverable.id}/status`;
    // console.log("**************** urL:", url);

    delete deliverable.id; // id is sent in the URL above.

    const keys = Object.keys(deliverable);
    // Prefix prop name with 'next' (eg. 'status: 1' => 'nextStatus: 1').
    const nameValPairs = keys.reduce((acc, curVal) => {
      acc['next' + curVal.charAt(0).toUpperCase() + curVal.slice(1)] = deliverable[curVal];
      return acc;
    }, {});

    //console.log("**** NextValues:", nameValPairs);
    // send the next values to update the DB.
    const response = await Axios.put(url, nameValPairs);
    if (response) {
      return response.data;
    }
    return undefined;
    // return data; // (readyStages, Array<readyDelis>).
  }

  // Send request to update the status of a number of deliverables.
  async updateDeliverableStatuses(
    orderId: OrderDTO['id'],
    deliverableStatusChanges: Array<Partial<SDeliverableStatusChangeDTO>>
  ) {

    const url = `orders/${orderId}/deliverable-statuses`;
    const { data } = await Axios.put(url, deliverableStatusChanges);

    return data; // Dictionary<int, (string, int[], Array<DeliverableDTO>, Array<DeliverableDueDate>
  }

  getInfoFromListResponse(data: { [key: string]: ListRequestResponse<JobRequest> }) {
    const { jobRequests: { items, pageInfo } } = data;

    return { items, pageInfo };
  }

  createCollection(
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequestId: JobRequestDTO['id'],
    data: CollectionDTO
  ): Promise<void> {
    const url = `orders/${orderId}/assets/${assetId}${this.urlPrefix}/${jobRequestId}/collections`;

    return Axios.post(url, data);
  }

  getCollection = async (
    collectionId: CollectionDTO['id']
  ) => {
    const url = `orders/assets${this.urlPrefix}/collections/${collectionId}`;
    const { data } = await Axios.get(url);

    return new Collection(data);
  }

  editCollection(
    collectionId: CollectionDTO['id'],
    data: CollectionDTO
  ): Promise<void> {
    const url = `orders/assets${this.urlPrefix}/collections/${collectionId}`;

    return Axios.put(url, data);
  }

  deleteCollection(
    collectionId: CollectionDTO['id'],
    orderId: OrderDTO['id'],
    assetId: AssetDTO['id'],
    jobRequestId: JobRequestDTO['id']
  ): Promise<void> {
    const url = `orders/${orderId}/assets/${assetId}${this.urlPrefix}/${jobRequestId}/collections/${collectionId}`;

    return Axios.delete(url);
  }

  async updateCollectionStatus(
    orderId: OrderDTO['id'],
    jobRequestId: JobRequestDTO['id'],
    collection: { id: CollectionDTO['id']; status: CollectionDTO['status'] }
  ) {
    try {
      const url = `orders/${orderId}/job-requests/${jobRequestId}/collections/${collection.id}/status`;

      const { data } = await Axios.put<Array<DeliverableStatus>>(url, { nextStatus: collection.status });

      return data;
    } catch (err) {
      throw err;
    }
  }
}
